import { useMemo } from "react";

import { CustomOption, CustomSingleValue } from "@/components/desktop/list/table/cells/SelectCell/CustomSelectComponents";
import EditableSelectCell from "@/components/desktop/list/table/cells/SelectCell/EditableSelectCell";
import EditableTextCell from "@/components/desktop/list/table/cells/TextCell/EditableTextCell";
import CustomBadge from "@/components/desktop/badge/CustomBadge";

import { APPLICATION_ORDER_TYPES } from "@/constants/applicationOrderTypes";

export function useProgramApplicationColumns(lookups, handleSave, editingRowId) {
	return useMemo(
		() => [
			{
				Header: "Tipo",
				accessor: "application_order_type_name",
				width: "5%",
				disableSortBy: true,
				Cell: ({ value, row }) => {
					if (!value)
						return (
							<CustomBadge variant="neutral" size="sm">
								—
							</CustomBadge>
						);
					const variant = APPLICATION_ORDER_TYPES[row.original.application_order_type_id] || "neutral";
					return (
						<CustomBadge variant={variant} size="sm">
							{value}
						</CustomBadge>
					);
				},
			},
			{
				Header: "Especie",
				accessor: "crop_text",
				width: "6%",
				Cell: ({ value, row }) => {
					const rowId = `${row.original.id}_${row.original.application_number}`;
					const isEditing = editingRowId === rowId;

					if (!isEditing) return <span>{value || "—"}</span>;
					return (
						<EditableSelectCell
							value={row.original.crop_id}
							row={row}
							fieldName="crop_id"
							onSave={handleSave}
							options={(lookups?.crops || []).filter((c) => c.value !== 0)}
							isClearable={false}
							placeholder="Seleccione especie..."
							components={{
								Option: CustomOption,
								SingleValue: CustomSingleValue,
							}}
						/>
					);
				},
			},
			{
				Header: "Variedad",
				accessor: "crop_detail_text",
				width: "6%",
				Cell: ({ value, row }) => {
					const rowId = `${row.original.id}_${row.original.application_number}`;
					const isEditing = editingRowId === rowId;

					const filteredVarietyOptions = (lookups?.crop_details || []).filter((cropDetail) => cropDetail.crop_customer_id === row.original.crop_id);

					if (!isEditing) return <span>{value || "—"}</span>;
					return (
						<EditableSelectCell
							value={row.original.crop_detail_id}
							row={row}
							fieldName="crop_detail_id"
							onSave={handleSave}
							options={filteredVarietyOptions}
							isClearable={false}
							placeholder="Seleccione variedad..."
							components={{
								Option: CustomOption,
								SingleValue: CustomSingleValue,
							}}
						/>
					);
				},
			},
			{
				Header: "Época",
				accessor: "period_text",
				width: "5%",
				Cell: ({ value, row }) => {
					const rowId = `${row.original.id}_${row.original.application_number}`;
					const isEditing = editingRowId === rowId;

					if (!isEditing) return <span>{value || "—"}</span>;
					return <EditableTextCell value={value} row={row} fieldName="period_text" onSave={handleSave} placeholder="Ingrese época..." />;
				},
			},
			{
				Header: "N°",
				accessor: "application_number",
				width: "3%",
				disableSortBy: true,
				Cell: ({ value, row }) => {
					const rowId = `${row.original.id}_${row.original.application_number}`;
					const isEditing = editingRowId === rowId;

					if (!isEditing) return <CustomBadge variant="primary">{value}</CustomBadge>;
					return (
						<EditableTextCell
							value={value}
							row={row}
							fieldName="application_number"
							onSave={handleSave}
							inputType="number"
							placeholder="N°"
							displayFormatter={(val) => <CustomBadge variant="primary">{val}</CustomBadge>}
						/>
					);
				},
			},
			{
				Header: "Estado Fenológico",
				accessor: "phenological_state_text",
				width: "7%",
				Cell: ({ value, row }) => {
					const rowId = `${row.original.id}_${row.original.application_number}`;
					const isEditing = editingRowId === rowId;

					if (!isEditing) return <span>{value || "—"}</span>;
					return (
						<EditableSelectCell
							value={row.original.phenological_state_id}
							row={row}
							fieldName="phenological_state_id"
							onSave={handleSave}
							options={lookups?.phenological_states || []}
							placeholder="Seleccione estado..."
							components={{
								Option: CustomOption,
								SingleValue: CustomSingleValue,
							}}
						/>
					);
				},
			},
			{
				Header: "Objetivo",
				accessor: "objective_text",
				width: "7%",
				Cell: ({ value, row }) => {
					const rowId = `${row.original.id}_${row.original.application_number}`;
					const isEditing = editingRowId === rowId;

					if (!isEditing) return <span>{value || "—"}</span>;
					return (
						<EditableSelectCell
							value={row.original.objective_id}
							row={row}
							fieldName="objective_id"
							onSave={handleSave}
							options={lookups?.objectives || []}
							placeholder="Seleccione objetivo..."
							components={{
								Option: CustomOption,
								SingleValue: CustomSingleValue,
							}}
						/>
					);
				},
			},
		],
		[lookups, handleSave, editingRowId],
	);
}
